import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, IconButton, Stack, Box, Tooltip, Chip } from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SpinWheelPicker from '../common/SpinWheelPicker';

const BAR_WEIGHTS = [0, 15, 25, 35, 45, 55, 65];
const TARGET_WEIGHTS = Array.from({ length: 101 }, (_, i) => i * 5);
const PLATE_WEIGHTS = [2.5, 5, 10, 25, 35, 45];

const calculatePlatesNeeded = (targetWeight, barWeight) => {
  const weightPerSide = (targetWeight - barWeight) / 2;
  if (weightPerSide <= 0) return {};
  const plates = {};
  let remaining = weightPerSide;
  for (const plateWeight of [...PLATE_WEIGHTS].reverse()) {
    if (remaining >= plateWeight) {
      const count = Math.floor(remaining / plateWeight);
      plates[plateWeight] = count;
      remaining = Math.round((remaining - (count * plateWeight)) * 100) / 100;
    }
  }
  return plates;
};

const SpinWheelTrigger = ({ value, onClick, icon: Icon }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      cursor: 'pointer',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      px: 0.75,
      py: 0.25,
      borderRadius: 1,
      bgcolor: 'background.paper',
      border: '1px dashed',
      borderColor: 'action.disabled',
      '&:hover': { 
        bgcolor: 'action.hover',
        borderColor: 'primary.light',
      },
      transition: 'all 0.15s',
    }}
  >
    {Icon && <Icon sx={{ fontSize: '1rem' }} color="action" />}
    <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', minWidth: '24px', textAlign: 'center' }}>
      {value}
    </Typography>
  </Box>
);

const ActivePlateChip = ({ weight, count, onClick, onContextMenu, editable }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Chip
      label={weight % 1 === 0 ? weight : weight.toFixed(1)}
      size="small"
      onClick={onClick}
      onContextMenu={onContextMenu}
      sx={{
        minWidth: '32px',
        height: '22px',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        cursor: onClick ? 'pointer' : 'default',
        border: editable ? '1px dashed' : 'none',
        borderColor: editable ? 'primary.light' : 'transparent',
        '&:hover': onClick ? { 
          bgcolor: 'primary.dark',
        } : {},
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
    <Typography
      variant="caption"
      sx={{
        fontSize: '0.6rem',
        color: 'primary.main',
        fontWeight: 'bold',
        lineHeight: 1,
        mt: 0.125,
      }}
    >
      ×{count}
    </Typography>
  </Box>
);

const InactivePlate = ({ weight, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 0.5,
      py: 0.25,
      borderRadius: 1,
      border: '1px dashed',
      borderColor: 'action.disabled',
      cursor: 'pointer',
      transition: 'all 0.15s',
      '&:hover': { 
        borderColor: 'primary.light',
        bgcolor: 'action.hover',
      },
    }}
  >
    <Typography
      sx={{
        fontSize: '0.75rem',
        color: 'text.secondary',
      }}
    >
      {weight % 1 === 0 ? weight : weight.toFixed(1)}
    </Typography>
  </Box>
);

const WeightPlateWidget = ({ defaultBarWeight = 45 }) => {
  const [barWeight, setBarWeight] = useState(defaultBarWeight);
  const [plates, setPlates] = useState({});
  const [mode, setMode] = useState('calc');
  const [targetWeight, setTargetWeight] = useState(defaultBarWeight);
  const [barAnchor, setBarAnchor] = useState(null);
  const [targetAnchor, setTargetAnchor] = useState(null);

  const filteredTargetWeights = useMemo(() => {
    return TARGET_WEIGHTS.filter(w => w >= barWeight);
  }, [barWeight]);

  useEffect(() => {
    if (targetWeight < barWeight) {
      setTargetWeight(barWeight);
    }
  }, [barWeight, targetWeight]);

  const buildTotal = useMemo(() => {
    const plateWeight = Object.entries(plates).reduce((sum, [weight, count]) => {
      return sum + (parseFloat(weight) * count * 2);
    }, 0);
    return barWeight + plateWeight;
  }, [barWeight, plates]);

  const calcPlates = useMemo(() => {
    return calculatePlatesNeeded(targetWeight, barWeight);
  }, [targetWeight, barWeight]);

  const displayPlates = mode === 'calc' ? calcPlates : plates;
  const hasPlates = Object.keys(displayPlates).some(k => displayPlates[k] > 0);

  const addPlate = (weight) => {
    setPlates(prev => ({ ...prev, [weight]: (prev[weight] || 0) + 1 }));
  };

  const removePlate = (weight) => {
    setPlates(prev => {
      const count = prev[weight] || 0;
      if (count <= 1) {
        const { [weight]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [weight]: count - 1 };
    });
  };

  const handleReset = () => {
    if (mode === 'build') {
      setPlates({});
    } else {
      setTargetWeight(barWeight);
    }
  };

  const toggleMode = () => {
    const newMode = mode === 'calc' ? 'build' : 'calc';
    setMode(newMode);
    if (newMode === 'calc') {
      setTargetWeight(Math.max(buildTotal, barWeight));
    } else {
      setPlates(calcPlates);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.5,
        px: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        boxShadow: 1,
        bgcolor: 'background.paper',
      }}
    >
      {/* Mode Switcher */}
      <Tooltip title={mode === 'calc' ? 'Switch to Plate Builder (Reverse)' : 'Switch to Weight Calculator'}>
        <IconButton
          size="small"
          onClick={toggleMode}
          sx={{
            p: 0.5,
            color: 'action.active',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <SwapHorizIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>

      {/* Target Weight (calc mode only — shown first as the goal) */}
      {mode === 'calc' && (
        <Tooltip title="Set your desired weight">
          <Box
            onClick={(e) => setTargetAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              px: 1,
              py: 0.375,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
              transition: 'all 0.15s',
              boxShadow: 1,
            }}
          >
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 'bold', minWidth: '24px', textAlign: 'center' }}>
              {targetWeight}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.55rem', opacity: 0.8 }}>
              lb
            </Typography>
          </Box>
        </Tooltip>
      )}

      <SpinWheelPicker
        values={filteredTargetWeights}
        value={targetWeight}
        onChange={setTargetWeight}
        open={Boolean(targetAnchor)}
        anchorEl={targetAnchor}
        onClose={() => setTargetAnchor(null)}
      />

      {mode === 'calc' && (
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'text.secondary' }}>=</Typography>
      )}

      {/* Bar Weight Selector */}
      <Tooltip title="Bar weight">
        <Box>
          <SpinWheelTrigger
            value={barWeight}
            onClick={(e) => setBarAnchor(e.currentTarget)}
            icon={FitnessCenterIcon}
          />
        </Box>
      </Tooltip>
      <SpinWheelPicker
        values={BAR_WEIGHTS}
        value={barWeight}
        onChange={setBarWeight}
        open={Boolean(barAnchor)}
        anchorEl={barAnchor}
        onClose={() => setBarAnchor(null)}
      />

      {/* Plates — shown after bar weight */}
      {mode === 'calc' && hasPlates && (
        <>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'text.secondary' }}>+</Typography>
          <Stack direction="row" spacing={0.25} alignItems="flex-start">
            {Object.entries(displayPlates)
              .filter(([_, c]) => c > 0)
              .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
              .map(([w, c]) => (
                <ActivePlateChip key={w} weight={parseFloat(w)} count={c} editable={false} />
              ))}
          </Stack>
        </>
      )}

      {mode === 'build' && (
        <>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'text.secondary' }}>+</Typography>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            {PLATE_WEIGHTS.map((weight) => {
              const count = plates[weight] || 0;
              if (count > 0) {
                return (
                  <ActivePlateChip
                    key={weight}
                    weight={weight}
                    count={count}
                    onClick={() => addPlate(weight)}
                    onContextMenu={(e) => { e.preventDefault(); removePlate(weight); }}
                    editable={true}
                  />
                );
              }
              return (
                <InactivePlate
                  key={weight}
                  weight={weight}
                  onClick={() => addPlate(weight)}
                />
              );
            })}
          </Stack>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'text.secondary' }}>=</Typography>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontFamily: 'monospace',
              fontSize: '1rem',
              minWidth: '40px',
              textAlign: 'center',
              color: hasPlates ? 'primary.main' : 'text.primary',
            }}
          >
            {buildTotal}
            <Typography component="span" sx={{ fontSize: '0.6rem', color: 'text.secondary', ml: 0.25 }}>
              lb
            </Typography>
          </Typography>
        </>
      )}

      <IconButton size="small" onClick={handleReset} disabled={!hasPlates && targetWeight === barWeight} sx={{ p: 0.5, ml: 'auto' }}>
        <RefreshIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Paper>
  );
};

WeightPlateWidget.propTypes = {
  defaultBarWeight: PropTypes.number,
};

export default WeightPlateWidget;